/**
 * Complete Onboarding Tab Component
 * Final validation and restaurant creation step
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Building, 
  CreditCard,
  Globe,
  Printer,
  Laptop,
  MapPin,
  Truck,
  Users,
  ArrowRight,
  Rocket
} from 'lucide-react';
import { SuperAdminOnboardingData, calculateOnboardingProgress, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface CompleteOnboardingTabProps {
  data: SuperAdminOnboardingData;
  onCreateRestaurant: () => Promise<void>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

interface ValidationItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  status: OnboardingStatus;
  required: boolean;
  description: string;
}

const CompleteOnboardingTab: React.FC<CompleteOnboardingTabProps> = ({
  data,
  onCreateRestaurant,
  onStatusUpdate
}) => {
  const [creating, setCreating] = useState(false);

  // Get overall progress
  const progress = calculateOnboardingProgress(data);

  // Define validation items
  const validationItems: ValidationItem[] = [
    {
      id: 'business_details',
      title: 'Business Details',
      icon: Building,
      status: data.business_details_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Complete business information, legal details, and contact information'
    },
    {
      id: 'stripe_connect',
      title: 'Payment Processing',
      icon: CreditCard,
      status: data.stripe_connection_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Stripe Connect setup for payment processing'
    },
    {
      id: 'domain_setup',
      title: 'Custom Domain',
      icon: Globe,
      status: data.dns_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Custom domain configuration and DNS setup'
    },
    {
      id: 'printer_setup',
      title: 'Receipt Printer',
      icon: Printer,
      status: data.printer_setup_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Receipt printer configuration for order processing'
    },
    {
      id: 'website_integration',
      title: 'Website Integration',
      icon: Laptop,
      status: data.website_linked_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Integration with existing website or platform'
    },
    {
      id: 'google_business',
      title: 'Google My Business',
      icon: MapPin,
      status: data.google_ordering_status || OnboardingStatus.NOT_STARTED,
      required: true,
      description: 'Google My Business profile and ordering integration'
    },
    {
      id: 'aggregators',
      title: 'Delivery Platforms',
      icon: Truck,
      status: data.uber_aggregation_status || data.doordash_aggregation_status || OnboardingStatus.NOT_STARTED,
      required: false,
      description: 'UberEats and DoorDash integration (optional)'
    }
  ];

  // Check completion status
  const requiredItems = validationItems.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.status === OnboardingStatus.COMPLETE);
  const allRequiredComplete = completedRequired.length === requiredItems.length;
  const hasBlockingIssues = validationItems.some(item => 
    item.required && item.status === OnboardingStatus.NEEDS_ATTENTION
  );

  // Handle restaurant creation
  const handleCreateRestaurant = async () => {
    if (!allRequiredComplete) {
      toast({
        title: "Cannot complete onboarding",
        description: "All required sections must be completed first",
        variant: "destructive"
      });
      return;
    }

    try {
      setCreating(true);
      await onCreateRestaurant();
      
      toast({
        title: "Restaurant created successfully!",
        description: "The onboarding process is now complete",
      });
    } catch (error) {
      console.error('Error creating restaurant:', error);
    } finally {
      setCreating(false);
    }
  };

  // Get status icon
  const getStatusIcon = (status: OnboardingStatus) => {
    switch (status) {
      case OnboardingStatus.COMPLETE:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <div className="h-4 w-4 border border-muted-foreground rounded-full" />;
    }
  };

  const isComplete = data.onboarding_status === OnboardingStatus.COMPLETE;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Rocket className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Complete Onboarding</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Final validation and restaurant creation
                </p>
              </div>
            </div>
            {isComplete && (
              <Badge variant="default" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Complete
              </Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Onboarding Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm text-muted-foreground">
                  {progress.completedStages}/{progress.totalStages} stages complete
                </span>
              </div>
              <Progress value={progress.progressPercentage} className="h-3" />
            </div>
            <div className="text-2xl font-bold text-primary">
              {progress.progressPercentage}%
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-lg font-semibold text-green-700 dark:text-green-300">
                {completedRequired.length}
              </div>
              <div className="text-sm text-green-600 dark:text-green-400">Required Complete</div>
            </div>
            
            <div className="p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-lg font-semibold text-blue-700 dark:text-blue-300">
                {requiredItems.length - completedRequired.length}
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">Remaining</div>
            </div>
            
            <div className="p-3 bg-purple-50 dark:bg-purple-950/50 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
                {validationItems.filter(item => !item.required && item.status === OnboardingStatus.COMPLETE).length}
              </div>
              <div className="text-sm text-purple-600 dark:text-purple-400">Optional Complete</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Onboarding Checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {validationItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.id} className="flex items-start gap-4 p-4 rounded-lg border">
                <div className="p-2 bg-muted rounded-lg">
                  <Icon className="h-5 w-5" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{item.title}</h4>
                    {item.required && (
                      <Badge variant="outline" className="text-xs">Required</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  {getStatusIcon(item.status)}
                  <Badge variant={getStatusBadgeVariant(item.status)} className="text-xs">
                    {getStatusDisplayName(item.status)}
                  </Badge>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Status Alerts */}
      {hasBlockingIssues && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Some required sections need attention before the onboarding can be completed. 
            Please review and resolve any issues in the sections marked as "Needs Attention".
          </AlertDescription>
        </Alert>
      )}

      {!allRequiredComplete && !hasBlockingIssues && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Complete all required sections to enable restaurant creation. 
            {requiredItems.length - completedRequired.length} section(s) remaining.
          </AlertDescription>
        </Alert>
      )}

      {allRequiredComplete && !isComplete && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            All required sections are complete! You can now create the restaurant and finish the onboarding process.
          </AlertDescription>
        </Alert>
      )}

      {/* User Information Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customer Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">User Details</h4>
              <div className="text-sm space-y-1">
                <div><strong>Name:</strong> {data.user_name}</div>
                <div><strong>Email:</strong> {data.user_email}</div>
                <div><strong>Current Role:</strong> {data.user_role}</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Business Details</h4>
              <div className="text-sm space-y-1">
                <div><strong>Restaurant:</strong> {data.restaurant_name || 'Not specified'}</div>
                <div><strong>Organisation:</strong> {data.organisation_name || 'Not specified'}</div>
                <div><strong>Created:</strong> {new Date(data.user_created_at).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Complete Onboarding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isComplete ? (
            <div className="p-6 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                Onboarding Complete!
              </h3>
              <p className="text-sm text-green-800 dark:text-green-200">
                The restaurant has been successfully created and the user has been upgraded to Restaurant Admin.
                The onboarding process is now complete.
              </p>
            </div>
          ) : (
            <>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">What happens when you complete onboarding?</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• A new restaurant will be created in the system</li>
                  <li>• The user's role will be upgraded to "Restaurant Admin"</li>
                  <li>• All onboarding data will be used to populate the restaurant profile</li>
                  <li>• The user will gain access to the full restaurant management dashboard</li>
                  <li>• Email notifications will be sent to confirm completion</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleCreateRestaurant}
                  disabled={!allRequiredComplete || creating || hasBlockingIssues}
                  className="flex-1"
                  size="lg"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Restaurant...
                    </>
                  ) : (
                    <>
                      <Rocket className="h-4 w-4 mr-2" />
                      Create Restaurant & Complete Onboarding
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>

              {!allRequiredComplete && (
                <p className="text-sm text-muted-foreground text-center">
                  Complete all required sections to enable restaurant creation.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompleteOnboardingTab;